/**
 * CompanyNameOnboarding — top-of-dashboard reminder that appears whenever
 * the signed-in user has not yet set their company_name on their profile.
 *
 * - Inline save: user can fill the field right from the dashboard
 * - Persists to PUT /api/profile with company_name + the user's existing full_name
 * - Once saved, the banner disappears (driven by user.company_name from AuthContext)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Building2, ArrowRight } from 'lucide-react';

const CompanyNameOnboarding = () => {
  const { user, currentWorkspace, updateUser } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Hide as soon as the user has any company_name set.
  if (!user || user.company_name) return null;
  // Super admin and auditors don't represent a customer org — skip the prompt
  // for them. Role can live on the user object or on currentWorkspace
  // (emulation flow), so we guard against both.
  const role = user.role || currentWorkspace?.role;
  if (role === 'super_admin' || role === 'auditor') return null;
  if (currentWorkspace?.id === 'super_admin') return null;

  const save = async (e) => {
    e?.preventDefault?.();
    const cleaned = value.trim();
    if (!cleaned) {
      toast.error('Company name is required');
      return;
    }
    try {
      setSaving(true);
      await axios.put('/api/profile',
        { full_name: user.full_name || user.email, company_name: cleaned },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      updateUser({ company_name: cleaned, full_name: user.full_name || user.email });
      toast.success('Company name saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save company name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40 mb-6" data-testid="company-name-onboarding">
      <CardContent className="py-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 shrink-0">
            <Building2 className="w-5 h-5 text-amber-700" strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-semibold text-slate-900">Finish setting up your account</p>
            <p className="text-xs text-slate-600">
              Tell us your company name — we’ll use it on audit reports, evidence exports, and your account summary.
            </p>
          </div>
          <form onSubmit={save} className="flex items-center gap-2 shrink-0">
            <Input
              type="text"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. Acme, Inc."
              maxLength={120}
              className="h-9 w-56 text-sm"
              data-testid="company-onboarding-input"
              disabled={saving}
            />
            <Button
              type="submit"
              size="sm"
              disabled={saving || !value.trim()}
              data-testid="company-onboarding-save-btn"
            >
              {saving ? 'Saving…' : 'Save'}
              {!saving && <ArrowRight className="w-3.5 h-3.5 ml-1" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/profile')}
              data-testid="company-onboarding-profile-btn"
            >
              Open Profile
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyNameOnboarding;
